define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"dojo/_base/fx",
	"dojo/_base/lang",
	"dojo/dom-class",
	"dojo/dom-construct",
	"dojo/dom-style",
	"dojo/fx",
	"dojo/mouse",
	"dojo/topic",
	"dijit/_WidgetBase",
	"csi",
	"csi/action/Popup",
	"csi/constants",
	"csi/def/Action",
	"csi/def/ViewRef",
	"csi/heading/_base",
	"csi/heading/_TooltipMixin",
	"csi/heading/Renderer",
	"csi/heading/Spec",
	"csi/locale",
	"csi/session",
	"csi/table/matrix/_Processor",
	"csi/types",
	"csi/xml",
	"../_CompletionRendererMixin"
], function(array, declare, baseFx, lang, domClass, domConstruct, domStyle, fx, mouse, topic, _WidgetBase,
	csi, Popup, constants, Action, ViewRef, csiHeading, _TooltipMixin, Renderer, Spec, locale, session, _Processor, types,
	xml, _CompletionRendererMixin) {

	// Activity Report node example:
	//
	// Node Name = "Accessory1"
	// Node Type = "ActivityReport"
	// Activity = "C5032"
	// Calendar = "C4968"
	// CalendarContainer = "centric://APPAREL/Style/1"
	// CompletionDate = 1354937565
	// ExpectedEndDate = 1353225600
	// Icon = "ActivityIcon:Star"
	// Item = "C5080"
	// Value = "1/1 100%"

	var STATE_ICON_COLORS = {
		0: "Red",
		1: "Yellow",
		2: "Lime",
		3: "Green",
		4: ""
	};

	var _MultiActivity = declare(_WidgetBase, {
		leftPosition: null,
		_bOverActivity: false,
		_bShowTooltip: false,

		startup: function() {
			this.own(
				topic.subscribe("/csi/tooltip/show", lang.hitch(this, this._showTooltip)),
				topic.subscribe("/csi/tooltip/hide", lang.hitch(this, this._hideTooltip))
			);

			this.on(mouse.enter, lang.hitch(this, this._enter));
			this.on(mouse.leave, lang.hitch(this, this._leave));
			this.inherited(arguments);
		},

		_enter: function() {
			// summary: animate to full height
			const {domNode} = this;
			const {x, y} = domNode.getBoundingClientRect();

			domClass.add(domNode, "timelineExpanded");
			domStyle.set(domNode, {
				position: "fixed",	// use fixed so popup will appear on top of other table cells
				left: `${x}px`,
				top: `${y - 2}px`,	// subtract 2 to offset top margin in timelineExpanded
			});

			fx.wipeIn({
				node: domNode,
				onEnd: () => {
					// make sure class is added - sometimes seems to get removed during animation
					domClass.add(domNode, "timelineExpanded");
				}
			}).play();

			this._bOverActivity = true;
		},

		_leave: function() {
			// summary: animate back to original height
			if (!this._bShowTooltip) {
				const {domNode} = this;

				baseFx.animateProperty({
					node: domNode,
					properties: {
						height: {
							end: 16
						}
					},
					onEnd: () => {
						domClass.remove(domNode, "timelineExpanded");
						domStyle.set(domNode, {
							position: "absolute",
							left: this.leftPosition,
							top: "",
						});
					}
				}).play();
			}

			this._bOverActivity = false;
		},

		_showTooltip: function() {
			// summary: if tooltip is shown while mouse expanded, then do not hide until tooltip is hidden
			this._bShowTooltip = this._bOverActivity;
		},

		_hideTooltip: function() {
			// summary: if tooltip is hidden and no longer over activity, then leave activity
			if (this._bShowTooltip && !this._bOverActivity) {
				this._bShowTooltip = false;
				this._leave();
			} else {
				this._bShowTooltip = false;
			}
		}
	});

	var _Renderer = declare([Renderer, _TooltipMixin], {
		constructor: function(/*String*/type, /*String?*/format) {
			// summary: initialize headings for tooltip
			this.addTooltipHeading(null, {
				m_id: "ActivityName",
				m_name: locale.type("BaseActivity")
			});

			this.addTooltipHeading(null, {
				m_id: "Value",
				m_name: locale.typeId("BaseActivity", "Completeness")
			});

			this.addTooltipHeading(null, {
				m_id: "Quantity",
				m_name: locale.resource("Quantity")
			});

			this.addTooltipHeading(null, {
				m_id: "CompletionDate",
				m_name: locale.typeId("BaseActivity", "CompletionDate")
			});

			this.addTooltipHeading(null, {
				m_id: "ExpectedEndDate",
				m_name: locale.typeId("BaseActivity", "ExpectedEndDate")
			});
		},

		toHtmlStatic: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode, /*ActionData*/actionData) {
			// summary: get correct icon based on activity state
			var act = ctx.getNode();
			domClass.add(refNode, h.getMatrixProcessor()._getActivityCSS(xml.enumGetText(act.Icon), act.State));
			return true;
		},

		toTooltip: function(/*Context*/ctx, /*Heading*/h, /*Tooltip*/tooltip) {
			// summary: add content and actions to tooltip
			var node = ctx.getNode();

			tooltip.addAction(this._mixinTooltipAction(lang.mixin(new Popup(), {
				m_id: "CalendarDetails",
				m_viewRef: lang.mixin(new ViewRef(), {
					m_id: "__Host__",
					m_url: node.Activity
				}),
				m_postAction: "postActionRedrawNode"
			}), h, true));

			if (xml.isValidURL(node.Link)) {
				tooltip.addAction(this._mixinTooltipAction(lang.mixin(new Action(), {
					m_id: "CalendarTimelineOpenItem",
					m_onAction: function(ac) {
						window.open(session.getLocationForUrl(ac.getNode().Link), "Timeline");
					}
				}), h, true));
			}

			this.renderTooltip(tooltip, ctx);
		}
	});

	return declare([_Processor, _CompletionRendererMixin], {
		restrictQuery: true,

		_cssActivity: "timelineActivity",
		_widthActivity: 22,
		_widthWeek: {
			1: 280,	// 40 pixels per day
			3: 210,	// 30
			5: 154,	// 22
			7: 154,	// 22
			9: 154,	// 22
			11: 154,// 22
			13: 154	// 22
		},

		getColumnSpecs: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return column specifications for matrix
			var a = [];
			var n = ctx.view.timelineWeeks;
			var dtPresent = ctx.view.timelinePresentDate;
			var dt = ctx.view.timelineStartDate;
			var dt2 = types.addDaysToDate(dt, 7, true);
			var columnWidth = this._widthWeek[n];

			h.classRenderer = _Renderer;

			for (var i = 0; i < n; ++i) {
				a.push(new Spec({
					css: constants.CSS_TABLE_COLUMNS,
					heading: h,
					headerParams: {
						className: (dt.getTime() == dtPresent.getTime()) ? " csi-table-column-highlight-color" : "",
						innerText: types.getDateRangeString(dt, dt2),
					},
					params: {
						dtBegin: dt,
						dtEnd: dt2,
						weeks: n
					},

					// fix column width and restrict resizing
					getColumnWidth: () => columnWidth,
					getColumnWidthKey: () => ""
				}));

				dt = types.addDaysToDate(dt, 7, true);
				dt2 = types.addDaysToDate(dt2, 7, true);
			}

			return a;
		},

		render: function(/*Context*/ctx, /*QueryResult*/qr, /*Spec*/spec, /*Number*/nColumn, /*DOMNode*/td) {
			// summary: render heading with given index
			var ren = csiHeading.getClassRenderer(_Renderer);
			var dtBegin = spec.params.dtBegin;
			var dtEnd = spec.params.dtEnd;
			var width = this._widthWeek[spec.params.weeks] / 7;
			var offset = (width - this._widthActivity) / 2;
			var activities = {};
			var h = spec.heading;
			var b;

			// find activities in this date range
			array.forEach(ctx.view.timelineActivities[ctx.getNodeURL()], function(act) {
				var dt = types.getTimeDate(act.CompletionDate || act.ExpectedEndDate);
				if ((dt >= dtBegin) && (dt < dtEnd)) {
					csi.objectArrayAddValue(activities, types.getDaysToDate(dtBegin, dt), act);
					act.State = act.State || this._getActivityState(act.ExpectedEndDate, act.CompletionDate, act.Value, act.PastDue);
					b = true;
				}
			}, this);

			if (b) {
				ctx.pushParams({
					urlNode: csi.EMPTY_URL,
					urlParent: null,
					idRelation: null
				});

				// activities exist for date range
				domStyle.set(td, "padding", "0");

				var cell = domConstruct.create("div", {
					"class": "timelineActivities"
				}, td);

				// plot activities per day
				csi.applyForEach(activities, function(acts, day) {
					const left = (day * width) + offset + "px";

					var div = domConstruct.create("div", {
						"class": this._cssActivity,
						style: {
							position: "absolute",
							left
						}
					}, cell);

					var minState = 1000;

					array.forEach(csi.sort(acts, "State"), function(act) {
						// render activity
						ren.headingToHtml(ctx.setNode(act), h, domConstruct.create("span", null, div));

						// track max state in case overlapping activities
						minState = Math.min(minState, act.State);
					}, this);

					// if multiple activities in cell, add placeholder that will support drop-down of all activities
					if (acts.length > 1) {
						domConstruct.create("span", {
							"class": this._getActivityCSS("Multiple", minState)
						}, div, "first");

						new _MultiActivity({
							leftPosition: left
						}, div).startup();
					}
				}, this);

				ctx.pop();
			} else {
				td.innerHTML = csi.EMPTY_CHAR;
			}

			return ++nColumn;
		},

		_getActivityCSS: function(/*String*/icon, /*String*/state) {
			// summary: return icon css given activity icon name and status
			return "csiActionIcon iconActivityIcon" + icon + STATE_ICON_COLORS[state];
		}
	});
});